import React, { useCallback, useMemo, useState } from "react";
import List from "@mui/material/List";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ListItem from "@mui/material/ListItem";
import Collapse from "@mui/material/Collapse";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Tooltip } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Icon from "@mui/material/Icon";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import PropTypes from "prop-types";

import { closeSidebar } from "../../store/ducks/duckSidebar";
import { useStylesLayout } from "../../styles/Layout";
import { actionToLog } from "../../store/ducks/duckAudit";

const SidebarItem = (props) => {
  const classes = useStylesLayout();
  const [open, setOpen] = useState(false);

  const history = useHistory();

  const { open: isSidebarOpen, paths } = useSelector((state) => state.sidebar);

  const {
    childrens,
    name,
    icon,
    selectedName,
    setSelectedName,
    layout,
    isRoot = false,
    isInternal,
    internalLink,
    id: menuItemId,
    groups,
    onNavigateStart,
  } = props;
  const displayName = name === "FinancialClosure" ? "Financial Closure" : name;
  const dispatch = useDispatch();

  const { id: idFromUrl } = useParams();
  const { pathname } = useLocation();
  const blurActiveElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const handleClick =
    (inNewTab = false) =>
    (e) => {
      e.preventDefault();

      let pathMenu = layout && paths ? paths[layout.id] : pathname;

      if (isInternal) {
        //Si es interno
        localStorage.setItem("menuItemIdCustom", null);
        localStorage.setItem("menuItemIdCustom", menuItemId);
        if (inNewTab) {
          window.open(
            `${window.location.origin}/Reports/${internalLink}`,
            "_blank",
            "noopener,noreferrer",
          );
        } else {
          onNavigateStart?.({ name, internalLink, isInternal, layout, menuItemId });
          history.push(`/${internalLink}`);
        }
        blurActiveElement();
        dispatch(closeSidebar());
      }
      if (layout) {
        //Si contiene layout
        if (idFromUrl === layout.id) {
          setSelectedName(name);
        }
        if (inNewTab) {
          window.open(
            `${window.location.origin}/Reports/Layout/${layout.id}`,
            "_blank",
            "noopener,noreferrer",
          );
        } else {
          onNavigateStart?.({ name, internalLink, isInternal, layout, menuItemId });
          history.push(`/Layout/${layout.id}`);
        }
        blurActiveElement();
        dispatch(closeSidebar());
      } else {
        setOpen(!open);
        setSelectedName("");
      }

      dispatch(
        actionToLog({
          action: "CLICK",
          category: "SIDEBAR",
          resource: "MenuItem",
          resourceId: menuItemId,
          pathMenu,
          details: JSON.stringify({ isInternal, inNewTab, groups }),
        }),
      );
    };

  const handleOpen = useCallback(() => {
    setOpen(!open);
    setSelectedName("");
  }, [open, setSelectedName]);

  const listItems = useMemo(
    () =>
      childrens?.map((subOption) => (
        <SidebarItem
          key={subOption.name}
          {...subOption}
          selectedName={selectedName}
          setSelectedName={setSelectedName}
          onNavigateStart={onNavigateStart}
        />
      )),
    [childrens, onNavigateStart, selectedName, setSelectedName],
  );

  return (
    <>
      <ListItem
        disableGutters
        dense
        selected={name === selectedName}
        sx={{ width: "100%", maxWidth: "100%", pl: isRoot ? 0 : 1 }}
      >
        <ListItemIcon style={{ color: "white", cursor: "pointer" }} onClick={handleClick(false)}>
          {isRoot && icon && <Icon>{icon}</Icon>}
          {!isRoot && (
            <DoubleArrowIcon
              fontSize="small"
              style={{ color: "rgba(255,255,255, 0.4)", paddingLeft: 4 }}
            />
          )}
        </ListItemIcon>

        {layout ? (
          <Tooltip title={`Go to ${displayName}`}>
            <ListItemText
              className={classes.text}
              data-custom-name={displayName}
              primary={displayName}
              primaryTypographyProps={{ noWrap: displayName.length > 17 }}
              sx={{ flex: "1 1 auto", margin: 0, textAlign: "left" }}
              style={{ cursor: "pointer" }}
              onClick={handleClick(false)}
            />
          </Tooltip>
        ) : (
          <ListItemText
            className={classes.text}
            data-custom-name={displayName}
            primary={displayName}
            primaryTypographyProps={{ noWrap: displayName.length > 17 }}
            sx={{ flex: "1 1 auto", margin: 0, textAlign: "left" }}
            style={{ cursor: "pointer" }}
            onClick={handleClick(false)}
          />
        )}
        {(layout || isInternal) && (
          <Tooltip style={{ cursor: "pointer" }} title="Open in a new Tab">
            <OpenInNewIcon
              data-custom-name={displayName}
              fontSize="small"
              style={{ color: "rgba(255, 255, 255, 0.3)", cursor: "pointer" }}
              onClick={handleClick(true)}
            />
          </Tooltip>
        )}

        {childrens && childrens.length > 0 && open ? (
          <ExpandLessIcon
            className={classes.icon}
            style={{ cursor: "pointer" }}
            onClick={handleOpen}
          />
        ) : childrens && childrens.length > 0 && !open ? (
          <ExpandMoreIcon
            className={classes.icon}
            style={{ cursor: "pointer" }}
            onClick={handleOpen}
          />
        ) : null}
      </ListItem>
      {childrens && childrens.length > 0 && (
        <Collapse
          unmountOnExit
          in={open && isSidebarOpen}
          style={{ marginLeft: 0, width: "100%" }}
          timeout="auto"
        >
          <List disablePadding component="div" sx={{ width: "100%", pl: 2 }}>
            {listItems}
          </List>
        </Collapse>
      )}
    </>
  );
};

SidebarItem.propTypes = {
  childrens: PropTypes.array,
  name: PropTypes.string,
  icon: PropTypes.string,
  selectedName: PropTypes.string,
  setSelectedName: PropTypes.func,
  layout: PropTypes.object,
  isRoot: PropTypes.bool,
  isInternal: PropTypes.bool,
  internalLink: PropTypes.string,
  onNavigateStart: PropTypes.func,
};

export default React.memo(SidebarItem);
